/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  notification,
  Radio,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CarOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  MailOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import moment from "moment/min/moment-with-locales";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_DD_MM_YYYY_HH_mm } from "constants/DateConstant";
import utils from "utils";
import { Trans, useTranslation } from "react-i18next";
import { CURRENCY, DELIVERY_DATE_WARNING_DAYS, DELIVERY_OPTIONS } from "constants/DeliveryConstants";
import { vceFirestore } from "../../../configs/FirebaseConfig";
import { sendShipmentToTrunkrs } from "../../../functions/api/trunkrs";
import { useCategories, useOrders, useProducts } from "../../../data/hooks";
import Text from "antd/lib/typography/Text";
import {
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_CREATED,
  ORDER_STATUS_KLARNA_AUTHORIZED,
  ORDER_STATUS_PAID,
  ORDER_STATUS_SHIPPED,
} from "../../../constants/OrderConstant";
import { useReactToPrint } from "react-to-print";
import { generateInvoice, getPickListData, getSendListData } from "../../../functions/orders";
import PickListTable from "../../../components/shared-components/Table/PickListTable";
import { formatInvoiceFileName, formatOrderDate, formatPrice, getPaymentStatus } from "../../../functions/formatters";
import _ from "lodash";
import locale from "antd/es/date-picker/locale/nl_NL";
import { createDeliveryOrder } from "functions/api/optimoroute";
import styled from "styled-components";
import { downloadFile, isMobile } from "../../../functions/browser";
import axios from "axios";
import { getFirebaseIDToken } from "../../../functions/firebase/firebase";
import OrderListTable from "../../../components/shared-components/Table/OrderListTable";
import PackingSlip from "../../../components/shared-components/Table/PackingSlip";
import SendLabel from "../../../components/shared-components/Table/SendLabel";
import * as dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

// Single DatePicker - no need for RangePicker anymore
// const ExcelFile = ReactExport.ExcelFile;
// const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;

const Orders = () => {
  const promiseResolveRef = useRef(null);

  const orderListRef = useRef();
  const pickListRef = useRef();
  const packingSlipRef = useRef();
  const combinedPackingSendRef = useRef();
  const sendListRef = useRef();

  // Single date for filtering (default to today)
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const deliveryDateStr = useMemo(() => selectedDate.format(DATE_FORMAT_DD_MM_YYYY), [selectedDate]);

  // Use custom hooks for data fetching with selected date
  const {
    orders,
    isLoading: ordersLoading,
    updateOrder,
    findOrderById: findOrderByIdHook,
    refreshOrders,
  } = useOrders({ realtime: true, deliveryDate: deliveryDateStr });
  const { products, isLoading: productsLoading } = useProducts();
  const { categories, isLoading: categoriesLoading } = useCategories();
  const [editOrderForm] = Form.useForm();
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [order, setOrder] = useState(null);
  const { t } = useTranslation();
  const hasWeightProperty = order ? order.products.some((item) => item.variation && item.variation.weight) : false;
  const [datePickerForm] = Form.useForm();
  const [editOrderModalVisible, setEditOrderModalVisible] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [deliveryType, setDeliveryType] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [pickupLocation, setPickupLocation] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [findingById, setFindingById] = useState(false);

  // Combined loading state
  const dataLoading = ordersLoading || productsLoading || categoriesLoading;

  moment.locale("nl");

  // Use useMemo instead of useState + useEffect to prevent infinite loops
  const filteredList = useMemo(() => {
    if (searchText) {
      return utils.wildCardSearchOnOrder(orders, searchText, null);
    }
    return orders;
  }, [orders, searchText]);

  // We watch for the state to change here, and for the Promise resolve to be available
  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const enrichOrdersWithProductData = (orders, products) => {
    // Early return if no products data available
    if (!products || products.length === 0) {
      return orders;
    }

    // Create a lookup map with shopSource-specific keys for O(1) product access
    // Key format: "productId|shopSource" to handle same products in different shops
    const productMap = products.reduce((map, product) => {
      const key = `${product.id}|${product.shopSource}`;
      map[key] = product;
      return map;
    }, {});

    // Enrich each order's products with data from the products collection
    return orders.map((order) => ({
      ...order,
      products: order.products.map((orderProduct) => {
        // Look up product using order's shopSource to get the correct variant
        const productKey = `${orderProduct.productId}|${order.shopSource}`;
        const productData = productMap[productKey];

        const productItems =
          productData?.productItems?.length > 0
            ? productData.productItems.map((p) => {
                // Also look up sub-products with shopSource
                const subProductKey = `${p.productId}|${order.shopSource}`;
                const foundProduct = productMap[subProductKey];
                if (!foundProduct) return null;
                return {
                  ...p,
                  name: foundProduct.name,
                  frozen: foundProduct?.frozen || false,
                };
              })
            : [];
        return {
          ...orderProduct,
          frozen: productData?.frozen || false,
          productItems,
        };
      }),
    }));
  };

  const showDrawer = (row) => {
    setVisible(true);
    setOrder(row);
  };

  const closeDrawer = () => {
    setVisible(false);
  };

  const formatShippingMethod = (value) => {
    if (value === DELIVERY_OPTIONS.SEND) return t("shipping");
    if (value === DELIVERY_OPTIONS.PICKUP) return t("pickup");
  };

  const getPaymentStatusColor = (status) => {
    if (status === ORDER_STATUS_PAID) return "geekblue";
    else if (status === ORDER_STATUS_SHIPPED) return "lime";
    else if (status === ORDER_STATUS_COMPLETED) return "cyan";
    else if (status === ORDER_STATUS_CREATED) return "red";
    else if (status === ORDER_STATUS_KLARNA_AUTHORIZED) return "orange";
  };

  const getShopBadgeColor = (shopSource) => {
    return shopSource === "VCE" ? "green" : "purple";
  };

  const handleManualPrint = (functionName) => {
    setIsPrinting(true);
    setTimeout(() => {
      functionName();
    }, 1000);
  };

  const handleOrderListPrint = useReactToPrint({
    contentRef: orderListRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handlePickListPrint = useReactToPrint({
    contentRef: pickListRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handlePackingSlipPrint = useReactToPrint({
    contentRef: packingSlipRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });
  //
  const handleSendListPrint = useReactToPrint({
    contentRef: sendListRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleCombinedPackingSendPrint = useReactToPrint({
    contentRef: combinedPackingSendRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleOptimoRouteExport = async () => {
    // Only VCE orders with delivery.type === "send" and landelijk === false (regional)
    const allOrders = getOrdersReadyToShip();
    const ordersToExport = allOrders.filter((order) => order.shopSource === "VCE" && order.landelijk === false);

    if (ordersToExport.length === 0) {
      message.warning("Geen VCE regionale verzendbestellingen geselecteerd");
      return;
    }

    if (ordersToExport.length > 0) {
      setLoading(true);
      await createDeliveryOrder(ordersToExport, vceFirestore).catch(() => {
        setLoading(false);
      });
      setLoading(false);

      const idToken = await getFirebaseIDToken();
      // Send email to Trustpilot for review (only to non gmail addresses, gmail get sent separately)
      const emailList = ordersToExport.map((order) => {
        return {
          email: order.customer.email,
          name: order.customer.firstName + " " + order.customer.lastName,
          deliveryDate: dayjs(order.delivery.date, "DD-MM-YYYY").format("YYYY-MM-DD"),
          orderDate: dayjs.unix(order.createdAt).format("YYYY-MM-DD"),
          orderNumber: order.orderId,
          trustpilotReviewInvitationSent: order.trustpilotReviewInvitationSent,
        };
      });
      const emailListWithoutGmail = _.uniqBy(
        emailList.filter((object) => !object.email.includes("gmail") && !object.trustpilotReviewInvitationSent),
        "email",
      );

      if (emailListWithoutGmail.length === 0) return;

      await axios
        .post(process.env.REACT_APP_VCE_API_URL + "/api/marketing/trustpilot/review", emailListWithoutGmail, {
          headers: {
            Authorization: `${idToken}`,
          },
        })
        .catch((err) => console.log(err));

      // Set invitationSent to true for all orders using updateOrder hook
      for (const order of ordersToExport) {
        await updateOrder(order, { trustpilotReviewInvitationSent: true });
      }
    }

    // Refresh orders to show updated status immediately
    await refreshOrders();
  };

  const handleTrunkrsExport = async () => {
    // VCE orders with delivery.type === "send" and landelijk === true
    // HVO orders with delivery.type === "send"
    const allOrders = getOrdersReadyToShip();
    const ordersToExport = allOrders.filter(
      (order) => (order.shopSource === "VCE" && order.landelijk === true) || order.shopSource === "HVO",
    );

    if (ordersToExport.length === 0) {
      message.warning("Geen landelijke verzendbestellingen geselecteerd");
      return;
    }

    setLoading(true);
    for (const row of ordersToExport) {
      const isLastRow = ordersToExport.indexOf(row) === ordersToExport.length - 1;
      if (row.orderStatus !== ORDER_STATUS_SHIPPED) {
        const delivery = row.customer.address.delivery;
        let weight = 0;
        row.products.forEach((product) => {
          const productWeight =
            !!product.variation && !!product.variation.weight
              ? product.quantity * parseInt(product.variation.weight)
              : 0;
          weight += productWeight;
        });
        const object = {
          orderReference: row.orderId,
          sender: {
            name: row.shopSource === "VCE" ? "Het Vleescentrum" : "Halalvlees Online",
            companyName: row.shopSource === "VCE" ? "Het Vleescentrum" : "Halalvlees Online",
            emailAddress: row.shopSource === "VCE" ? "info@hetvleescentrum.nl" : "info@halalvlees-online.nl",
            address: "De Hoek 91A",
            postalCode: "1601MR",
            city: "Enkhuizen",
            country: "NL",
          },
          recipient: {
            name: row.customer.firstName + " " + row.customer.lastName,
            companyName: row.customer.companyName || "",
            emailAddress: row.customer.email,
            address:
              delivery.street + " " + delivery.houseNumber + (!!delivery.addition ? " " + delivery.addition : ""),
            postalCode: delivery.zipCode,
            city: delivery.city,
            country: delivery.country || "NL",
          },
          parcel: [
            {
              description: row.orderId,
              weight: {
                value: weight,
                unit: "g",
              },
              contents: row.products.map((product) => {
                return {
                  name: product.name,
                  reference: product.productId,
                };
              }),
            },
          ],
          service: "SAME_DAY",
        };
        try {
          const trunkrsNumber = await sendShipmentToTrunkrs(object);
          let firestoreObject = {
            orderStatus: ORDER_STATUS_SHIPPED,
          };
          if (!!trunkrsNumber) firestoreObject.trunkrsNumber = trunkrsNumber;

          // Use updateOrder hook for consistent updates with optimistic UI
          await updateOrder(row, firestoreObject);

          // Show success message when last row is exported
          if (isLastRow) {
            message.success(t("orders_exported_to_trunkrs"));
            setLoading(false);
          }
          setLoading(false);
        } catch (err) {
          notification.error({
            message: t("notifications.error_saving"),
            description: row.orderId + ": " + err.toString().replace("Error: ", ""),
            duration: 0,
          });
          setLoading(false);
        }
      } else {
        if (isLastRow) {
          message.success(t("orders_exported_to_trunkrs"));
          setLoading(false);
        }
      }
    }

    if (process.env.REACT_APP_ENVIRONMENT === "production") {
      // Send orders to API to send a mail to the customer
      const vceOrders = [];
      const hvoOrders = [];
      ordersToExport.forEach((order) => {
        const object = {
          orderId: order.orderId,
          email: order.customer.email,
          firstName: order.customer.firstName,
          lastName: order.customer.lastName,
        };
        if (order.shopSource === "VCE") vceOrders.push(object);
        else hvoOrders.push(object);
      });

      const endpoint = "/api/email/order/picked_up";
      // Send mail to Vleescentrum customers
      if (vceOrders.length > 0) {
        axios.post(process.env.REACT_APP_VCE_API_URL + endpoint, vceOrders).catch((err) => {
          console.log(err);
        });
      }

      // Send mail to Halalvlees Online customers
      if (hvoOrders.length > 0) {
        axios.post(process.env.REACT_APP_HVO_API_URL + endpoint, hvoOrders).catch((err) => {
          console.log(err);
        });
      }
    }

    // Refresh orders to show updated status immediately
    await refreshOrders();
  };

  const getOrdersReadyToShip = () => {
    // Use selected rows if any, otherwise use all orders for selected date
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;

    const filteredOrders = _.filter(ordersToFilter, function (order) {
      return (
        [ORDER_STATUS_PAID, ORDER_STATUS_KLARNA_AUTHORIZED].includes(order.orderStatus) &&
        order.delivery.type === DELIVERY_OPTIONS.SEND
      );
    });

    // Remove all gift card only orders
    const filteredByGiftCard = _.filter(filteredOrders, function (order) {
      return !order.containsOnlyGiftCards;
    });
    // Order by date ascending
    return _.orderBy(filteredByGiftCard, ["createdAt"], ["asc"]);
  };

  const getHvoDeliveryEmails = () => {
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    const hvoDeliveryOrders = ordersToFilter.filter(
      (order) => order.shopSource === "HVO" && order.delivery.type === DELIVERY_OPTIONS.SEND,
    );
    const uniqueEmails = [...new Set(hvoDeliveryOrders.map((order) => order.customer.email))];
    const emails = uniqueEmails.join("; ");
    notification.open({
      message: "HVO bezorging e-mails",
      description: emails || "Geen e-mails gevonden",
      duration: 0,
    });
  };

  const getVceLandelijkEmails = () => {
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    const vceLandelijkOrders = ordersToFilter.filter(
      (order) =>
        order.shopSource === "VCE" && order.delivery.type === DELIVERY_OPTIONS.SEND && order.landelijk === true,
    );
    const uniqueEmails = [...new Set(vceLandelijkOrders.map((order) => order.customer.email))];
    const emails = uniqueEmails.join("; ");
    notification.open({
      message: "VCE bezorging landelijk e-mails",
      description: emails || "Geen e-mails gevonden",
      duration: 0,
    });
  };

  const getVcePickupEmails = () => {
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    const vcePickupOrders = ordersToFilter.filter(
      (order) => order.shopSource === "VCE" && order.delivery.type === DELIVERY_OPTIONS.PICKUP,
    );
    const uniqueEmails = [...new Set(vcePickupOrders.map((order) => order.customer.email))];
    const emails = uniqueEmails.join("; ");
    notification.open({ message: "VCE afhaal e-mails", description: emails || "Geen e-mails gevonden", duration: 0 });
  };

  const getVceRegionaalEmails = () => {
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    const vceRegionaalOrders = ordersToFilter.filter(
      (order) =>
        order.shopSource === "VCE" && order.delivery.type === DELIVERY_OPTIONS.SEND && order.landelijk === false,
    );
    const uniqueEmails = [...new Set(vceRegionaalOrders.map((order) => order.customer.email))];
    const emails = uniqueEmails.join("; ");
    notification.open({
      message: "VCE bezorging regionaal e-mails",
      description: emails || "Geen e-mails gevonden",
      duration: 0,
    });
  };

  const getFilteredOrders = (orderStatusFilter, deliveryType) => {
    // Use selected rows if any, otherwise use all orders for selected date
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;

    // Filter by order status
    let filteredOrders =
      orderStatusFilter === ORDER_STATUS_PAID
        ? filterOrdersRows(ordersToFilter, ORDER_STATUS_PAID)
        : filterOrdersRows(ordersToFilter, null);

    // Return only shipment orders if deliveryType == send
    if (!!deliveryType && deliveryType === DELIVERY_OPTIONS.SEND) {
      _.remove(filteredOrders, (item) => item.delivery.type === DELIVERY_OPTIONS.PICKUP);
    }

    // Return only pickup orders if deliveryType == pickup
    if (!!deliveryType && deliveryType === DELIVERY_OPTIONS.PICKUP) {
      _.remove(filteredOrders, (item) => item.delivery.type === DELIVERY_OPTIONS.SEND);
    }

    // Order by date ascending
    return _.orderBy(filteredOrders, ["createdAt"], ["asc"]);
  };

  // No longer needed - filterOrdersDateRange removed

  const filterOrdersRows = (orders, statusFilter) => {
    return !!statusFilter
      ? _.filter(orders, function (order) {
          return order.orderStatus === statusFilter;
        })
      : orders;
  };

  const CombinedPackingSend = React.forwardRef((props, ref) => {
    const { data } = props;

    return (
      <div ref={ref}>
        {data.map((order, index) => (
          <div key={order.id}>
            <PackingSlip data={enrichOrdersWithProductData([order], products)} />
            <div style={{ pageBreakAfter: "always" }}></div>
            <SendLabel data={getSendListData([order])} />
            {index < data.length - 1 && <div style={{ pageBreakAfter: "always" }}></div>}
          </div>
        ))}
      </div>
    );
  });

  const dropdownMenu = (row) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => openEditModal(row)}>
          <Flex alignItems="center">
            <EditOutlined />
            <span style={{ marginLeft: 4 }}>{t("edit")}</span>
          </Flex>
        </a>
      </Menu.Item>
      <Menu.Item>
        <a onClick={() => showDrawer(row)}>
          <Flex alignItems="center">
            <EyeOutlined />
            <span style={{ marginLeft: 4 }}>{t("details")}</span>
          </Flex>
        </a>
      </Menu.Item>
    </Menu>
  );

  const exportMenu = (
    <Menu>
      <Menu.Item key="1" onClick={handleOptimoRouteExport}>
        <CarOutlined /> <span>{t("optimoroute")} (VCE Regionaal)</span>
      </Menu.Item>
      <Menu.Item key="2" onClick={handleTrunkrsExport}>
        <CarOutlined /> <span>Trunkrs (Landelijk)</span>
      </Menu.Item>
    </Menu>
  );

  const printMenu = (
    <Menu>
      <Menu.Item key="2" onClick={() => handleManualPrint(handlePickListPrint)}>
        {t("order.pick_list")}
      </Menu.Item>
      <Menu.Item key="3" onClick={() => handleManualPrint(handlePackingSlipPrint)}>
        {t("order.packing_slips")}
      </Menu.Item>
      <Menu.Item key="4" onClick={() => handleManualPrint(handleSendListPrint)}>
        {t("order.send_labels")}
      </Menu.Item>
      <Menu.Item key="5" onClick={() => handleManualPrint(handleCombinedPackingSendPrint)}>
        {t("order.packing_slips_and_send_labels")}
      </Menu.Item>
      <Menu.SubMenu key="emails" title={<span>E-mailadressen</span>} icon={<MailOutlined />}>
        <Menu.Item key="email-hvo" onClick={getHvoDeliveryEmails}>
          HVO Bezorging
        </Menu.Item>
        <Menu.Item key="email-vce-landelijk" onClick={getVceLandelijkEmails}>
          VCE Bezorging Landelijk
        </Menu.Item>
        <Menu.Item key="email-vce-pickup" onClick={getVcePickupEmails}>
          VCE Afhaal
        </Menu.Item>
        <Menu.Item key="email-vce-regionaal" onClick={getVceRegionaalEmails}>
          VCE Bezorging Regionaal
        </Menu.Item>
      </Menu.SubMenu>
    </Menu>
  );

  const tableColumns = [
    {
      title: "Shop",
      dataIndex: "shopSource",
      render: (shopSource, record) => <Tag color={getShopBadgeColor(shopSource)}>{shopSource}</Tag>,
      sorter: (a, b) => a.shopSource.localeCompare(b.shopSource),
      width: 80,
    },
    {
      title: t("order.order_id"),
      dataIndex: "orderId",
    },
    {
      title: t("email"),
      dataIndex: "customer",
      render: (data) => <span>{data.email}</span>,
    },
    {
      title: t("user"),
      dataIndex: "customer",
      render: (data) => (
        <span>
          {data.firstName} {data.lastName}
        </span>
      ),
    },
    {
      title: t("order_date"),
      dataIndex: "createdAt",
      render: (data) => <span>{dayjs.unix(data).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "createdAt"),
      defaultSortOrder: "descend",
    },
    {
      title: t("pickup_delivery_date"),
      dataIndex: "delivery",
      render: (data) => (
        <span>{!!data && !!data.date ? dayjs(data.date, DATE_FORMAT_DD_MM_YYYY).format("ddd D MMMM YYYY") : "-"}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.delivery.date, DATE_FORMAT_DD_MM_YYYY).unix() - dayjs(b.delivery.date, DATE_FORMAT_DD_MM_YYYY).unix(),
    },
    {
      title: t("pickup_type"),
      dataIndex: "delivery",
      render: (data) => (
        <span>{!!data ? (data.type === DELIVERY_OPTIONS.SEND ? t("shipping") : t("pickup_short")) : "-"}</span>
      ),
    },
    {
      title: t("status"),
      dataIndex: "orderStatus",
      render: (_, order) => (
        <Tag color={getPaymentStatusColor(order.orderStatus)}>{getPaymentStatus(order.orderStatus)}</Tag>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "orderStatus"),
    },
    {
      title: t("total"),
      dataIndex: "totalPrice",
      render: (data) => <span className="font-weight-semibold">{CURRENCY + formatPrice(data)}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalPrice"),
    },
    {
      title: "",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const orderDetailsColumns = [
    {
      title: t("name"),
      render: (_, data) => (
        <div className="d-flex">
          <span>
            {data.name} {data.type && "(" + data.type + ")"}
          </span>
        </div>
      ),
    },
    {
      title: t("price"),
      dataIndex: "originalPrice",
      render: (data) => <span>€{formatPrice(data)}</span>,
      align: "center",
    },
    {
      title: t("weight"),
      dataIndex: "variation",
      render: (data) => (data && data.weight ? utils.formatWeight(data.weight) + t("gram_unit") : "-"),
      align: "center",
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      render: (data) => <span>{data}</span>,
      align: "center",
    },
    {
      title: t("discount"),
      dataIndex: "discountPercentage",
      render: (data) => <span>{data}%</span>,
      align: "center",
    },
    {
      title: t("sub_total"),
      dataIndex: "paymentPrice",
      render: (_, data) => <span>€{formatPrice(data.paymentPrice * data.quantity)}</span>,
      align: "right",
    },
  ];

  const rowSelection = {
    onChange: (key, rows) => {
      setSelectedRows(rows);
      setSelectedRowKeys(key);
    },
  };

  const onSearch = (e) => {
    const value = e.currentTarget.value;
    setSearchText(value);
    setSelectedRowKeys([]);
  };

  const findOrderById = async (value) => {
    const input = (value || orderIdSearch || "").trim();
    if (!input) {
      message.warning(t("enter_order_id"));
      return;
    }
    try {
      setFindingById(true);
      const foundOrder = await findOrderByIdHook(input);
      if (foundOrder) {
        showDrawer(foundOrder);
      } else {
        message.error(t("order_not_found", { orderId: input }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to find order by ID", err);
      message.error(t("order_not_found", { orderId: input }));
    } finally {
      setFindingById(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date || dayjs());
    setSelectedRows([]);
    setSelectedRowKeys([]);
  };

  const openEditModal = (order) => {
    setOrderToEdit(order);
    setDeliveryType(order.delivery.type);
    setDeliveryDate(dayjs(order.delivery.date, "DD-MM-YYYY"));
    setPickupLocation(order.delivery.pickupLocation || null);
    editOrderForm.setFieldsValue({
      deliveryType: order.delivery.type,
      deliveryDate: dayjs(order.delivery.date, "DD-MM-YYYY"),
      pickupLocation: order.delivery.pickupLocation || null,
    });
    setEditOrderModalVisible(true);
  };

  const closeEditModal = () => {
    setEditOrderModalVisible(false);
    setOrderToEdit(null);
    setDeliveryType(null);
    setDeliveryDate(null);
    setPickupLocation(null);
  };

  const handleUpdateOrder = async () => {
    let firestoreObject = _.clone(orderToEdit.delivery);
    let values = editOrderForm.getFieldsValue();
    firestoreObject.date = dayjs(values.deliveryDate).format("DD-MM-YYYY");
    firestoreObject.dateTimestamp = dayjs(values.deliveryDate).unix();
    firestoreObject.saturdayPickupSelected = dayjs(values.deliveryDate).weekday() === 5;
    firestoreObject.type = values.deliveryType;
    if (values.deliveryType === "pickup" && values.pickupLocation) {
      firestoreObject.pickupLocation = values.pickupLocation;
    }

    // Use updateOrder hook for consistent updates with optimistic UI
    const success = await updateOrder(orderToEdit, { delivery: firestoreObject });

    if (success) {
      message.success(t("order_saved"));
      closeEditModal();
      // Refresh orders to update the view when delivery date changes
      // This ensures the order disappears from current date if moved to another date
      await refreshOrders();
    } else {
      message.error(t("notifications.error_saving"));
    }
  };

  const downloadInvoice = (order) => {
    let invoiceOrder = {
      ...order,
      language: "nl",
      orderDate: formatOrderDate(order.createdAt),
    };
    generateInvoice(invoiceOrder).then((response) => {
      response.blob().then((blob) => downloadFile(blob, formatInvoiceFileName(t("invoice"), order.orderId)));
    });
  };

  // Calculate export statistics
  const exportStats = useMemo(() => {
    const stats = {
      vceOptimoroute: { total: 0, exported: 0 },
      trunkrs: { total: 0, exported: 0 },
    };

    orders.forEach((order) => {
      // Only count send orders (not pickup)
      if (order.delivery?.type === DELIVERY_OPTIONS.SEND) {
        const isShipped = order.orderStatus === ORDER_STATUS_SHIPPED;
        const isPaid = order.orderStatus !== ORDER_STATUS_CREATED;

        // Only count PAID or SHIPPED orders
        if (isPaid || isShipped) {
          // VCE regional orders -> OptimoRoute
          if (order.shopSource === "VCE" && order.landelijk === false) {
            stats.vceOptimoroute.total++;
            if (isShipped) {
              stats.vceOptimoroute.exported++;
            }
          }
          // VCE national orders + all HVO orders -> Trunkrs
          else if ((order.shopSource === "VCE" && order.landelijk === true) || order.shopSource === "HVO") {
            stats.trunkrs.total++;
            if (isShipped) {
              stats.trunkrs.exported++;
            }
          }
        }
      }
    });

    return stats;
  }, [orders]);

  return (
    <>
      <Card>
        {/* Date indicator banner */}
        <div
          style={{
            backgroundColor: "#f0f2f5",
            padding: "12px 16px",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <span style={{ fontWeight: 500, fontSize: "14px" }}>Aflever-/ophaaldatum:</span>
            <span style={{ fontSize: "16px", fontWeight: 600, color: "#1890ff" }}>
              {selectedDate.format("dddd D MMMM YYYY")}
            </span>
            <span style={{ marginLeft: "auto", color: "#8c8c8c" }}>
              {orders.length} {orders.length === 1 ? "order" : "orders"}
            </span>
          </div>
          {/* Export statistics */}
          <div style={{ display: "flex", gap: "16px", fontSize: "13px" }}>
            {exportStats.vceOptimoroute.total > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Tag color="green" style={{ margin: 0 }}>
                  VCE Regionaal
                </Tag>
                <span style={{ color: "#52c41a", fontWeight: 600 }}>{exportStats.vceOptimoroute.exported}</span>
                <span style={{ color: "#8c8c8c" }}>/ {exportStats.vceOptimoroute.total}</span>
                <span style={{ color: "#8c8c8c", fontSize: "12px" }}>→ OptimoRoute</span>
              </div>
            )}
            {exportStats.trunkrs.total > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Tag color="blue" style={{ margin: 0 }}>
                  Landelijk
                </Tag>
                <span style={{ color: "#1890ff", fontWeight: 600 }}>{exportStats.trunkrs.exported}</span>
                <span style={{ color: "#8c8c8c" }}>/ {exportStats.trunkrs.total}</span>
                <span style={{ color: "#8c8c8c", fontSize: "12px" }}>→ Trunkrs</span>
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Input
              value={searchText}
              placeholder={t("search")}
              prefix={<SearchOutlined />}
              onChange={(e) => onSearch(e)}
              style={{ width: "fit-content" }}
            />
            <Input.Search
              value={orderIdSearch}
              onChange={(e) => setOrderIdSearch(e.target.value)}
              placeholder={t("search_order_by_id")}
              enterButton={t("search")}
              loading={findingById}
              onSearch={findOrderById}
              style={{ width: "fit-content" }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Tooltip title={t("pickup_delivery_date")}>
              <Form form={datePickerForm}>
                <Form.Item name="deliveryDate" style={{ marginBottom: 0 }} initialValue={dayjs()}>
                  <DatePicker
                    value={selectedDate}
                    onChange={handleDateChange}
                    locale={locale}
                    placeholder={t("delivery_date")}
                    format={DATE_FORMAT_DD_MM_YYYY}
                    allowClear={false}
                  />
                </Form.Item>
              </Form>
            </Tooltip>
            <Dropdown overlay={printMenu}>
              <Button className="ant-btn-primary">
                <PrinterOutlined /> {t("print")} <DownOutlined />
              </Button>
            </Dropdown>
            <Dropdown overlay={exportMenu}>
              <Button className="ant-btn-primary">
                <FileExcelOutlined /> {t("export")} <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
        <div className="table-responsive">
          <Table
            showSorterTooltip={false}
            columns={tableColumns}
            dataSource={filteredList}
            rowKey="id"
            rowSelection={{
              selectedRowKeys: selectedRowKeys,
              type: "checkbox",
              preserveSelectedRowKeys: false,
              ...rowSelection,
            }}
            scroll={{ x: 1300 }}
            loading={dataLoading}
            onRow={(record, t) => {
              return {
                onClick: (e) => {
                  if (e.target.className.includes("ant-tag") || e.target.tagName === "A") return;
                  showDrawer(record);
                },
              };
            }}
          />
        </div>
        {!!order && (
          <Drawer
            title={`${order.orderId} - ${order.shopName}`}
            placement="right"
            onClose={closeDrawer}
            visible={visible}
            width={isMobile ? window.innerWidth : 720}
          >
            <Descriptions
              bordered
              title={
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{t("order.order")}</span>
                  <Tag color={getPaymentStatusColor(order.orderStatus)}>{getPaymentStatus(order.orderStatus)}</Tag>
                </div>
              }
              size="small"
              column={1}
            >
              <Descriptions.Item label={t("order_date")}>
                {dayjs.unix(order.createdAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}
              </Descriptions.Item>
              <Descriptions.Item label={t("name")}>
                {order.customer.firstName} {order.customer.lastName}
              </Descriptions.Item>
              <Descriptions.Item label={t("email")}>{order.customer.email}</Descriptions.Item>
              <Descriptions.Item label={t("phone")}>{order.customer.phoneNumber}</Descriptions.Item>
              <Descriptions.Item label={t("delivery_address")}>
                {order.customer.address.delivery.firstName} {order.customer.address.delivery.lastName} <br />
                {order.customer.address.delivery.companyName} {order.customer.address.delivery.companyName && <br />}
                {order.customer.address.delivery.street} {order.customer.address.delivery.houseNumber}{" "}
                {order.customer.address.delivery.addition}
                <br />
                {order.customer.address.delivery.zipCode}, {order.customer.address.delivery.city}
              </Descriptions.Item>
              {!!order.customer.address.invoice && (
                <Descriptions.Item label={t("invoice_address")}>
                  {order.customer.address.invoice.firstName} {order.customer.address.invoice.lastName} <br />
                  {order.customer.address.invoice.companyName} {order.customer.address.invoice.companyName && <br />}
                  {order.customer.address.invoice.street} {order.customer.address.invoice.houseNumber}{" "}
                  {order.customer.address.invoice.addition}
                  <br />
                  {order.customer.address.invoice.zipCode}, {order.customer.address.invoice.city}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t("shipping_method")}>
                {formatShippingMethod(order.delivery.type)}
              </Descriptions.Item>
              {order.delivery.type === DELIVERY_OPTIONS.PICKUP && order.delivery.pickupLocation && (
                <Descriptions.Item label={t("pickup_location")}>
                  {order.delivery.pickupLocation === "schepenwijk"
                    ? "Schepenwijk (De Hoek 91a001, Enkhuizen)"
                    : order.delivery.pickupLocation === "winkel"
                      ? "Winkel (Melkmarkt 10, Enkhuizen)"
                      : "-"}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t("pickup_delivery_date")}>
                {dayjs(order.delivery.date, DATE_FORMAT_DD_MM_YYYY).format("ddd D MMMM YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label={t("order_notes")}>
                {!!order.orderNotes ? order.orderNotes : "-"}
              </Descriptions.Item>
            </Descriptions>
            <div className="table-responsive pt-5">
              <h4>{t("products")}</h4>
              <Table
                showSorterTooltip={false}
                columns={
                  hasWeightProperty
                    ? orderDetailsColumns
                    : orderDetailsColumns.filter((col) => col.dataIndex !== "variation")
                }
                dataSource={order.products}
                pagination={false}
                rowKey="variantId"
                className="w-100"
                bordered={true}
                summary={(pageData) => {
                  let totalAmount = 0;
                  let totalDiscount = 0;

                  pageData.forEach(({ quantity, discount }) => {
                    totalAmount += quantity;
                    totalDiscount += discount;
                  });
                  return (
                    !!order && (
                      <>
                        {order.delivery.type === DELIVERY_OPTIONS.SEND && (
                          <Table.Summary.Row>
                            <Table.Summary.Cell>
                              <Text>{t("shipping")}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell />
                            <Table.Summary.Cell />
                            <Table.Summary.Cell />
                            {hasWeightProperty && <Table.Summary.Cell />}
                            <Table.Summary.Cell align="right">
                              <Text>{!!order.delivery.cost ? "€" + formatPrice(order.delivery.cost) : "-"}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                        {!!order.discount && (
                          <Table.Summary.Row>
                            <Table.Summary.Cell>
                              <Text>
                                {!!order.discount.percentage ? (
                                  <Trans
                                    i18nKey="cart_discount_percentage"
                                    values={{
                                      discountPercentage: order.discount.percentage,
                                      discountCode: order.discount.code,
                                    }}
                                  />
                                ) : (
                                  <Trans
                                    i18nKey="cart_discount_amount"
                                    values={{
                                      discountAmount: order.discount.amount,
                                      discountCode: order.discount.code,
                                    }}
                                  />
                                )}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell />
                            <Table.Summary.Cell />
                            <Table.Summary.Cell />
                            {hasWeightProperty && <Table.Summary.Cell />}
                            <Table.Summary.Cell align="right">
                              <Text>- {CURRENCY + formatPrice(order.discount.totalDiscount)}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                        <Table.Summary.Row>
                          <Table.Summary.Cell>
                            <Text strong={true}>{t("total")}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="center" />
                          {hasWeightProperty && <Table.Summary.Cell align="center" />}
                          <Table.Summary.Cell align="center">
                            <Text strong={true}>{totalAmount}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="center">
                            <Text strong={true}>€{formatPrice(totalDiscount)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong={true}>€{formatPrice(order.totalPrice)}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    )
                  );
                }}
              />
            </div>
            <Button className={"mt-4"} icon={<DownloadOutlined />} onClick={() => downloadInvoice(order)}>
              {t("download_invoice")}
            </Button>
          </Drawer>
        )}
        {editOrderModalVisible && (
          <Modal
            title={t("edit_order", { orderId: orderToEdit.orderId })}
            visible={editOrderModalVisible}
            onCancel={() => closeEditModal()}
            onOk={handleUpdateOrder}
            okText={t("save")}
            cancelText={t("close")}
          >
            <Form layout="vertical" form={editOrderForm} name="advanced_search" className="ant-advanced-search-form">
              <Form.Item
                name="deliveryType"
                label={t("shipping_method")}
                rules={[
                  {
                    required: true,
                    message: t("form.choose_delivery_type"),
                  },
                ]}
              >
                <Radio.Group onChange={(e) => setDeliveryType(e.target.value)}>
                  <Radio value={"send"} style={{ alignItems: "center" }}>
                    {t("shipping")}
                  </Radio>
                  <Radio value={"pickup"} style={{ alignItems: "center" }}>
                    {t("pickup")}
                  </Radio>
                </Radio.Group>
              </Form.Item>
              {deliveryType === "pickup" && (
                <Form.Item
                  name="pickupLocation"
                  label={t("pickup_location")}
                  rules={[
                    {
                      required: true,
                      message: "Kies een afhaallocatie",
                    },
                  ]}
                >
                  <Radio.Group onChange={(e) => setPickupLocation(e.target.value)}>
                    <Radio value={"schepenwijk"} style={{ alignItems: "center" }}>
                      Schepenwijk (De Hoek 91a001, Enkhuizen)
                    </Radio>
                    <Radio value={"winkel"} style={{ alignItems: "center" }}>
                      Winkel (Melkmarkt 10, Enkhuizen)
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              )}
              <Form.Item
                name="deliveryDate"
                label={t("delivery_date")}
                rules={[
                  {
                    required: true,
                    message: t("form.choose_delivery_date"),
                  },
                ]}
                className={"mb-1"}
              >
                <DatePicker
                  locale={locale}
                  format={DATE_FORMAT_DD_MM_YYYY}
                  placeholder={t("delivery_date")}
                  showToday={false}
                  inputReadOnly={true}
                  style={{ width: "100%" }}
                  onChange={(value) => setDeliveryDate(value)}
                />
              </Form.Item>
              {deliveryDate && deliveryDate.diff(dayjs(), "days") < 0 && (
                <DateWarningMessage>{t("order_delivery_date_warning_past")}</DateWarningMessage>
              )}
              {deliveryDate && deliveryDate.diff(dayjs(), "days") + 1 > DELIVERY_DATE_WARNING_DAYS && (
                <DateWarningMessage>
                  {t("order_delivery_date_warning", { days: deliveryDate.diff(dayjs(), "days") + 1 })}
                </DateWarningMessage>
              )}
            </Form>
          </Modal>
        )}
        <div style={{ display: "none" }}>
          {isPrinting && (
            <>
              <OrderListTable
                ref={orderListRef}
                data={enrichOrdersWithProductData(
                  selectedRows.length > 0 ? selectedRows : getFilteredOrders(),
                  products,
                )}
                orderDetailsColumns={orderDetailsColumns}
                hasWeightProperty={hasWeightProperty}
                formatShippingMethod={formatShippingMethod}
              />
              <PickListTable
                ref={pickListRef}
                data={getPickListData(enrichOrdersWithProductData(getFilteredOrders(), products), products, categories)}
              />
              <PackingSlip ref={packingSlipRef} data={enrichOrdersWithProductData(getFilteredOrders(), products)} />
              <SendLabel
                ref={sendListRef}
                data={getSendListData(enrichOrdersWithProductData(getFilteredOrders(), products))}
              />
              <CombinedPackingSend
                ref={combinedPackingSendRef}
                data={enrichOrdersWithProductData(getFilteredOrders(), products)}
              />
            </>
          )}
        </div>
      </Card>
    </>
  );
};

const DateWarningMessage = styled.p`
  color: #ff7300;
`;

export default Orders;
